import { useEffect } from "react";
import { create } from "mutative";
import { messageService, toolCallService } from "../services";
import {
  type Message,
  isAssistantActionRequestMessage,
  isReadyForSubmission,
  assertAssistantActionRequestMessage,
} from "../component/types";
import type { AIChatState } from "ee/reducers/uiReducers/aiChatReducer";
import log from "loglevel";
import {
  areAllToolCallsCompleted,
  getPendingSubmissionIndexes,
  getPendingSubmissionToolCalls,
  setToolCallsSubmissionStatus,
} from "../utils/toolCallUtils";

interface UseToolCallHandlingProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  updateState: (updates: Partial<AIChatState>) => void;
  startOperation: () => AbortSignal;
  stopOperation: () => void;
  updateFromStream: (response: Response, signal: AbortSignal) => Promise<void>;
  widgetId: string;
  queryId: string;
  activeEnv: string;
  viewMode: boolean;
  logError: (errorMessage: string) => void;
  isCancelling: boolean;
}

export function useToolCallHandling({
  activeEnv,
  isCancelling,
  logError,
  messages,
  queryId,
  setMessages,
  startOperation,
  stopOperation,
  updateFromStream,
  updateState,
  viewMode,
  widgetId,
}: UseToolCallHandlingProps) {
  useEffect(
    function handleToolCallExecution() {
      messages.forEach(async (message, messageIndex) => {
        if (!isAssistantActionRequestMessage(message)) return;
        // If the process is cancelled,
        // Don't execute or submit the tool call
        // Mark pending and inProgress tool calls as success
        // Mark pendingApproval tool calls as error

        if (isCancelling) {
          setMessages((prevMessages) =>
            create(prevMessages, (draft) => {
              const message = draft[messageIndex];

              if (isAssistantActionRequestMessage(message)) {
                // Update each tool call based on its current status
                message.content.forEach((toolCall, toolCallIndex) => {
                  if (
                    toolCall.executionStatus === "pending" ||
                    toolCall.executionStatus === "inProgress"
                  ) {
                    message.content[toolCallIndex].executionStatus = "success";
                  } else if (toolCall.executionStatus === "pendingApproval") {
                    message.content[toolCallIndex] = {
                      ...message.content[toolCallIndex],
                      executionStatus: "error",
                      error: "Process was cancelled",
                    };
                  }

                  // Mark toolCall outputSubmissionStatus as success on cancel
                  if (
                    toolCall.outputSubmissionStatus === "pending" ||
                    toolCall.outputSubmissionStatus === "inProgress"
                  ) {
                    message.content[toolCallIndex].outputSubmissionStatus =
                      "success";
                  }
                });
              }
            }),
          );

          return; // Don't execute or submit the tool call
        }

        if (isReadyForSubmission(message)) {
          log.debug("[FC]: actionRequest to submit", message);

          // Use utility functions for maintainability
          if (!areAllToolCallsCompleted(message.content)) return;

          const pendingToolCallIndexes = getPendingSubmissionIndexes(
            message.content,
          );
          const pendingToolCalls = getPendingSubmissionToolCalls(
            message.content,
          );

          if (pendingToolCalls.length === 0) return;

          setMessages((prevMessages) =>
            create(prevMessages, (draft) => {
              const message = draft[messageIndex];

              assertAssistantActionRequestMessage(message);
              setToolCallsSubmissionStatus(
                message.content,
                pendingToolCallIndexes,
                "inProgress",
              );
            }),
          );

          const signal = startOperation();

          try {
            updateState({ isWaitingForResponse: true });
            const response = await messageService.submitActionRequestOutput(
              {
                widgetId,
                queryId,
                runId: message.runId,
                threadId: message.threadId,
                prevMessageId: message.prevMessageId,
                prevResponseId: message.responseId,
                outputs: pendingToolCalls,
                activeEnv,
                viewMode,
              },
              signal,
            );

            await updateFromStream(response, signal);
            updateState({ isWaitingForResponse: false });

            setMessages((prevMessages) =>
              create(prevMessages, (draft) => {
                const message = draft[messageIndex];

                assertAssistantActionRequestMessage(message);
                setToolCallsSubmissionStatus(
                  message.content,
                  pendingToolCallIndexes,
                  "success",
                );
              }),
            );
          } catch (error) {
            if (signal.aborted) {
              return;
            }

            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";

            logError(errorMessage);
            updateState({ isWaitingForResponse: false, runId: "" });

            setMessages((prevMessages) =>
              create(prevMessages, (draft) => {
                const message = draft[messageIndex];

                assertAssistantActionRequestMessage(message);
                setToolCallsSubmissionStatus(
                  message.content,
                  pendingToolCallIndexes,
                  "error",
                );
              }),
            );
          } finally {
            stopOperation();
          }

          return;
        }

        // Execute tool calls
        message.content.forEach(async (toolCall, toolCallIndex) => {
          if (toolCall.executionStatus !== "pending") return;

          log.debug("[FC]: toolCall to execute", toolCall);

          setMessages((prevMessages) =>
            create(prevMessages, (draft) => {
              const message = draft[messageIndex];

              assertAssistantActionRequestMessage(message);

              message.content[toolCallIndex].executionStatus = "inProgress";
            }),
          );

          try {
            const signal = startOperation();
            const result = await toolCallService.executeToolCall(
              toolCall,
              signal,
            );

            if (result.status === "cancelled") {
              return;
            }

            setMessages((prevMessages) =>
              create(prevMessages, (draft) => {
                const message = draft[messageIndex];

                if (isAssistantActionRequestMessage(message)) {
                  if (result.status === "success") {
                    message.content[toolCallIndex] = {
                      ...message.content[toolCallIndex],
                      executionStatus: "success",
                      output: result.result as string,
                    };
                  } else {
                    message.content[toolCallIndex] = {
                      ...message.content[toolCallIndex],
                      executionStatus: "error",
                      error: result.error || "Unknown error",
                    };
                  }
                }
              }),
            );
          } catch (error) {
            setMessages((prevMessages) =>
              create(prevMessages, (draft) => {
                const message = draft[messageIndex];

                if (isAssistantActionRequestMessage(message)) {
                  const errorMessage =
                    error instanceof Error ? error.message : "Unknown error";

                  logError(errorMessage);

                  message.content[toolCallIndex] = {
                    ...message.content[toolCallIndex],
                    executionStatus: "error",
                    error: errorMessage,
                  };
                }
              }),
            );
          }
        });
      });
    },
    [
      messages,
      widgetId,
      queryId,
      activeEnv,
      viewMode,
      logError,
      updateState,
      startOperation,
      updateFromStream,
      setMessages,
      isCancelling,
      stopOperation,
    ],
  );
}
